import React, { useState, useEffect } from 'react';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from 'reactstrap';
import { fetchOrderDetails, fetchUserByMobile } from '../actions/action';
import toast from 'react-hot-toast';
import './SearchOrderOffcanvas.css';

const SearchOrderOffcanvas = ({ isOpen, toggle, onOrderSelect }) => {
  const [searchBy, setSearchBy] = useState('mobile');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searched, setSearched] = useState(false);
  const [notInList, setNotInList] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue) {
      toast.error('Please enter a search value');
      return;
    }

    setLoading(true);
    setSearched(true);
    setResults([]);
    setSelectedOrder(null);
    setNotInList(false);

    try {
      const params = {};
      if (searchBy === 'mobile') {
        // Remove leading zeros and add country code if missing
        let mobile = searchValue.replace(/^0+/, '');
        if (!mobile.startsWith('971')) {
          mobile = '971' + mobile;
        }
        params.value = mobile;
      }else {
        params.value = searchValue;
      }
      params.searchBy = searchBy;

      const response = await fetchOrderDetails(params);
      
      // API returns { code: 100, data: [...] }
      if (response && response.code === 100 && Array.isArray(response.data)) {
        setResults(response.data);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to fetch orders');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (order) => {
    setSelectedOrder(order);
  };

  const handleProceed = () => {
    if (selectedOrder) {
      onOrderSelect(selectedOrder);
      toggle();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setResults([]);
      setSelectedOrder(null);
      setSearched(false);
      setNotInList(false);
    }
  }, [isOpen]);

  const searchOptions = [
    { value: 'mobile', label: 'Mobile', icon: 'bx-mobile-alt' },
    { value: 'serial_no', label: 'Serial No', icon: 'bx-barcode' },
    { value: 'so_no', label: 'SO No', icon: 'bx-receipt' },
  ];

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '50vw', maxWidth: '650px' }}
    >
        <OffcanvasHeader toggle={toggle} style={{ borderBottom: '2px solid #f3f4f6', padding: '20px 24px' }}>
          <span style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>Search Order</span>
        </OffcanvasHeader>
        <OffcanvasBody style={{ padding: '24px' }}>
          {/* Search By Options */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '16px', fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Search By
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {searchOptions.map((option) => (
                <label
                  key={option.value}
                  className={`search-radio-option ${searchBy === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setSearchBy(option.value);
                    setSearchValue('');
                    setResults([]);
                    setSelectedOrder(null);
                    setSearched(false);
                  }}
                >
                  <input
                    type="radio"
                    name="searchBy"
                    checked={searchBy === option.value}
                    onChange={() => {}}
                    style={{ pointerEvents: 'none' }}
                  />
                  <i className={`bx ${option.icon} search-radio-icon`}></i>
                  <span className="search-radio-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Search Input */}
          <form onSubmit={handleSearch} style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              {searchBy === 'mobile' ? 'Mobile Number' : searchBy === 'serial_no' ? 'Serial Number' : 'SO Number'}
            </label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  className="search-input-field"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={`Enter ${searchBy === 'mobile' ? 'mobile number' : searchBy === 'serial_no' ? 'serial number' : 'SO number'}`}
                  required
                />
                {searchBy === 'mobile' && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', fontSize: '13px' }}>
                    Enter 9 digit UAE mobile number (e.g. 501234567)
                  </small>
                )}
              </div>
              <button type="submit" className="search-btn" disabled={loading}>
                {loading ? (
                  <Spinner size="sm" style={{ width: '18px', height: '18px' }} />
                ) : (
                  <>
                    <i className="bx bx-search" style={{ fontSize: '18px' }}></i>
                    Search
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Results Section */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Spinner style={{ width: '48px', height: '48px', color: '#2563eb' }} />
              <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '15px' }}>Searching for orders...</p>
            </div>
          ) : searched ? (
            results.length > 0 && !notInList ? (
              <div>
                <div style={{ marginBottom: '20px', padding: '12px 16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                    <i className="bx bx-check-circle" style={{ marginRight: '8px' }}></i>
                    {results.length} {results.length === 1 ? 'order' : 'orders'} found
                  </span>
                </div>
                
                <div style={{ marginBottom: '24px', maxHeight: '400px', overflowY: 'auto', paddingRight: '4px' }}>
                  {results.map((order, index) => (
                    <div
                      key={index}
                      className={`order-result-card ${selectedOrder === order ? 'selected' : ''}`}
                      onClick={() => handleSelect(order)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flex: 1 }}>
                          <input
                            type="radio"
                            className="order-radio"
                            checked={selectedOrder === order}
                            readOnly
                            style={{ marginTop: '4px' }}
                          />
                          <div style={{ flex: 1 }}>
                            <h6 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                              {order.customer_name}
                            </h6>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                <strong style={{ color: '#374151', fontWeight: '600' }}>SO Number:</strong> {order.so_no}
                              </div>
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                <strong style={{ color: '#374151', fontWeight: '600' }}>Product:</strong> {order.product_name}
                              </div>
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                <strong style={{ color: '#374151', fontWeight: '600' }}>Sku:</strong> {order.item_code}
                              </div>
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                <strong style={{ color: '#374151', fontWeight: '600' }}>Serial No:</strong> <span style={{ color: '#dc2626', fontWeight: '600' }}>{order.serial_no || 'Not Available'}</span>
                              </div>
                              {order.purchase_date && typeof order.purchase_date === 'string' && (
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                  <strong style={{ color: '#374151', fontWeight: '600' }}>Purchase Date:</strong> {order.purchase_date}
                                </div>
                              )}
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                <strong style={{ color: '#374151', fontWeight: '600' }}>Mobile:</strong> {order.Mobile_Number}
                              </div>
                              {order.Alternate_Mobile && (
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                  <strong style={{ color: '#374151', fontWeight: '600' }}>Alternate Mobile:</strong> {order.Alternate_Mobile}
                                </div>
                              )}
                              {order.is_amc === true && (
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                  <strong style={{ color: '#374151', fontWeight: '600' }}>AMC Period:</strong> {order.amc_start_date} - {order.amc_end_date}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {order.is_amc === true && (
                          <span className="store-badge">AMC</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    className="proceed-btn"
                    onClick={handleProceed}
                    disabled={!selectedOrder}
                  >
                    Select & Proceed
                  </button>
                  <button
                    className="proceed-btn"
                    onClick={() => setNotInList(true)}
                  >
                    Not in the list
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <i className="bx bx-search-alt" style={{ fontSize: '64px', color: '#d1d5db' }}></i>
                <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '15px', fontWeight: '500' }}>
                  No orders found for the given criteria.
                </p>
                <p style={{ marginTop: '8px', color: '#9ca3af', fontSize: '13px' }}>
                  Try searching with different criteria
                </p>
                <button
                  className="proceed-btn"
                  style={{ marginTop: '24px' }}
                  onClick={() => {
                    // Set a global flag to force showing the form in CreateServiceOrderOffcanvas
                    if (typeof window !== 'undefined') {
                      window.__forceShowServiceOrderForm = true;
                    }
                    // Only prefill mobile when the search was by mobile;
                    // for serial_no / so_no the searchValue isn't a phone number.
                    onOrderSelect({ mobile: searchBy === 'mobile' ? searchValue : '' });
                    toggle();
                  }}
                >
                  Proceed to Create
                </button>
              </div>
            )
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <i className="bx bx-search" style={{ fontSize: '64px', color: '#e5e7eb' }}></i>
              <p style={{ marginTop: '16px', color: '#9ca3af', fontSize: '15px' }}>
                Search for an order to create a service request
              </p>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
  );
};

export default SearchOrderOffcanvas;
